import React, { useRef, useState, useMemo } from "react";
import {
    Upload, FileText, FolderOpen, Search, CheckCircle,
    XCircle, AlertTriangle, Eye, Trash2, Download,
    ChevronRight, RefreshCw, X, FilePlus, Layers
} from "lucide-react";
import "../parametresStyles/ImportFichePaie.css";

/* ─── Mock known matricules (replace with redux selector) ─── */
const KNOWN_MATRICULES = ["EMP001","EMP002","EMP003","EMP004","EMP005","EMP006","EMP007","EMP008"];

/* ─── Helpers ─── */
const uid = () => Math.random().toString(36).slice(2, 8);

const fakeAnalyse = (file) => {
    const rand = Math.random();
    const matricule = `EMP00${Math.ceil(Math.random() * 10)}`;
    const found = KNOWN_MATRICULES.includes(matricule);
    return {
        id: uid(),
        name: file.name,
        size: (file.size / 1024).toFixed(1) + " KB",
        matricule,
        employe: found ? "Collaborateur " + matricule : null,
        periode: `${["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"][Math.floor(Math.random()*12)]} 2025`,
        status: !found ? "error" : rand > 0.15 ? "ok" : "warning",
        message: !found
            ? `Matricule ${matricule} introuvable dans le système`
            : rand > 0.15
                ? "Fiche valide"
                : "Période déjà importée – doublon possible",
    };
};

const StatusIcon = ({ status }) => {
    if (status === "ok")      return <CheckCircle  size={15} className="fp-icon-ok"   />;
    if (status === "warning") return <AlertTriangle size={15} className="fp-icon-warn" />;
    return                           <XCircle       size={15} className="fp-icon-err"  />;
};

const StatusBadge = ({ status }) => (
    <span className={`fp-badge fp-badge-${status}`}>
        {status === "ok" ? "Valide" : status === "warning" ? "Avertissement" : "Erreur"}
    </span>
);

/* ─── Modal ─── */
const Modal = ({ open, title, onClose, children, footer }) => {
    if (!open) return null;
    return (
        <div className="fp-overlay" onMouseDown={onClose}>
            <div className="fp-modal" onMouseDown={e => e.stopPropagation()}>
                <div className="fp-modal-header">
                    <h3>{title}</h3>
                    <button className="fp-modal-close" onClick={onClose}><X size={17}/></button>
                </div>
                <div className="fp-modal-body">{children}</div>
                {footer && <div className="fp-modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ─── Steps bar ─── */
const steps = ["Dépôt", "Analyse", "Validation", "Import"];
const StepsBar = ({ current }) => (
    <div className="fp-steps">
        {steps.map((s, i) => (
            <React.Fragment key={s}>
                <div className={`fp-step ${i < current ? "done" : i === current ? "active" : ""}`}>
                    <div className="fp-step-dot">{i < current ? <CheckCircle size={12}/> : i + 1}</div>
                    <span>{s}</span>
                </div>
                {i < steps.length - 1 && <div className={`fp-step-line ${i < current ? "done" : ""}`}/>}
            </React.Fragment>
        ))}
    </div>
);

/* ═══════════════════════════════════════
   Main Component
═══════════════════════════════════════ */
export default function ImportFichePaie() {
    const [step, setStep] = useState(0);
    const [files, setFiles] = useState([]);       // raw File objects
    const [results, setResults] = useState([]);   // analysed rows
    const [dragging, setDragging] = useState(false);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [preview, setPreview] = useState(null); // row for detail modal
    const [analysing, setAnalysing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [done, setDone] = useState(false);
    const fileInput = useRef();
    const folderInput = useRef();

    /* ── Add files (deduplicate by name) ── */
    const addFiles = (newFiles) => {
        const arr = Array.from(newFiles).filter(f => f.type === "application/pdf" || f.name.endsWith(".pdf") || f.name.endsWith(".xlsx") || f.name.endsWith(".xls"));
        if (!arr.length) return;
        setFiles(prev => {
            const names = new Set(prev.map(f => f.name));
            return [...prev, ...arr.filter(f => !names.has(f.name))];
        });
        setStep(s => Math.max(s, 0));
        setResults([]);
        setDone(false);
    };

    const removeFile = (name) => {
        setFiles(prev => prev.filter(f => f.name !== name));
        setResults(prev => prev.filter(r => r.name !== name));
    };

    /* ── Drag & Drop ── */
    const onDrop = (e) => {
        e.preventDefault(); setDragging(false);
        addFiles(e.dataTransfer.files);
    };

    /* ── Analyse ── */
    const analyse = () => {
        if (!files.length) return;
        setAnalysing(true);
        setStep(1);
        setTimeout(() => {
            setResults(files.map(fakeAnalyse));
            setAnalysing(false);
            setStep(2);
        }, 1400);
    };

    /* ── Import ── */
    const doImport = () => {
        const valid = results.filter(r => r.status !== "error");
        if (!valid.length) return;
        setImporting(true);
        setStep(3);
        setTimeout(() => {
            setImporting(false);
            setDone(true);
        }, 1600);
    };

    /* ── Stats ── */
    const stats = useMemo(() => ({
        total: results.length,
        ok:      results.filter(r => r.status === "ok").length,
        warn:    results.filter(r => r.status === "warning").length,
        errors:  results.filter(r => r.status === "error").length,
    }), [results]);

    /* ── Filtered rows ── */
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return results.filter(r => {
            const matchQ = !q || r.name.toLowerCase().includes(q) || r.matricule.toLowerCase().includes(q) || (r.employe || "").toLowerCase().includes(q);
            const matchF = filter === "all" || r.status === filter;
            return matchQ && matchF;
        });
    }, [results, query, filter]);

    const hasErrors = stats.errors > 0;
    const canImport = results.length > 0 && !analysing;

    /* ─────────────────── RENDER ─────────────────── */
    return (
        <div className="fp-page">

            {/* Header */}
            <div className="fp-header">
                <div className="fp-header-left">
                    <div className="fp-header-icon"><FileText size={20}/></div>
                    <div>
                        <h1>Import des fiches de paie</h1>
                        <p>Déposez des fichiers, analysez et importez en masse</p>
                    </div>
                </div>
                <div className="fp-header-actions">
                    <button className="fp-btn fp-btn-outline" onClick={() => { setFiles([]); setResults([]); setStep(0); setDone(false); }}>
                        <RefreshCw size={13}/> Réinitialiser
                    </button>
                    <button className="fp-btn fp-btn-outline" onClick={() => {}}>
                        <Download size={13}/> Modèle
                    </button>
                </div>
            </div>

            {/* Steps bar */}
            <div className="fp-steps-wrap">
                <StepsBar current={step}/>
            </div>

            {/* ── Drop zone + file list ── */}
            {!done && (
                <div className="fp-body">

                    {/* Left: drop zone */}
                    <div className="fp-left">
                        <div
                            className={`fp-dropzone ${dragging ? "dragging" : ""}`}
                            onDragOver={e => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={onDrop}
                        >
                            <div className="fp-drop-inner">
                                <div className="fp-drop-icon"><Upload size={28}/></div>
                                <p className="fp-drop-title">Glissez vos fiches ici</p>
                                <p className="fp-drop-sub">un fichier, plusieurs, ou un dossier entier</p>
                                <div className="fp-drop-actions">
                                    <button className="fp-btn fp-btn-primary" onClick={() => fileInput.current.click()}>
                                        <FilePlus size={13}/> Fichier(s)
                                    </button>
                                    <button className="fp-btn fp-btn-ghost" onClick={() => folderInput.current.click()}>
                                        <FolderOpen size={13}/> Dossier
                                    </button>
                                </div>
                                <input ref={fileInput}   type="file" multiple accept=".pdf,.xlsx,.xls" hidden onChange={e => addFiles(e.target.files)}/>
                                <input ref={folderInput} type="file" webkitdirectory="" hidden onChange={e => addFiles(e.target.files)}/>
                            </div>
                        </div>

                        {/* Loaded files chips */}
                        {files.length > 0 && (
                            <div className="fp-chips-wrap">
                                <div className="fp-chips-header">
                                    <Layers size={13}/> {files.length} fichier{files.length > 1 ? "s" : ""} chargé{files.length > 1 ? "s" : ""}
                                </div>
                                <div className="fp-chips">
                                    {files.map(f => (
                                        <div key={f.name} className="fp-chip">
                                            <FileText size={12}/>
                                            <span title={f.name}>{f.name.length > 28 ? f.name.slice(0,26)+"…" : f.name}</span>
                                            <button onClick={() => removeFile(f.name)}><X size={11}/></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Analyse CTA */}
                        <button
                            className={`fp-btn fp-btn-analyse ${files.length === 0 ? "disabled" : ""}`}
                            onClick={analyse}
                            disabled={files.length === 0 || analysing}
                        >
                            {analysing
                                ? <><span className="fp-spin"/>&nbsp;Analyse en cours…</>
                                : <><Eye size={14}/> Analyser & Prévisualiser</>
                            }
                            {!analysing && <ChevronRight size={14}/>}
                        </button>
                    </div>

                    {/* Right: results */}
                    <div className="fp-right">
                        {results.length === 0 && !analysing && (
                            <div className="fp-empty-results">
                                <div className="fp-empty-icon"><Eye size={30}/></div>
                                <p>Les résultats d'analyse s'afficheront ici</p>
                                <span>Déposez vos fichiers puis cliquez sur <strong>Analyser</strong></span>
                            </div>
                        )}

                        {analysing && (
                            <div className="fp-empty-results">
                                <span className="fp-spin fp-spin-lg"/>
                                <p style={{marginTop:14}}>Analyse des fiches…</p>
                            </div>
                        )}

                        {results.length > 0 && !analysing && (
                            <>
                                {/* Stats strip */}
                                <div className="fp-stats">
                                    <div className="fp-stat"><span className="fp-stat-num">{stats.total}</span><span>Total</span></div>
                                    <div className="fp-stat fp-stat-ok"><span className="fp-stat-num">{stats.ok}</span><span>Valides</span></div>
                                    <div className="fp-stat fp-stat-warn"><span className="fp-stat-num">{stats.warn}</span><span>Avertissements</span></div>
                                    <div className="fp-stat fp-stat-err"><span className="fp-stat-num">{stats.errors}</span><span>Erreurs</span></div>
                                </div>

                                {hasErrors && (
                                    <div className="fp-alert fp-alert-danger">
                                        <XCircle size={15}/> {stats.errors} fiche{stats.errors>1?"s":""} en erreur — les fichiers invalides ne seront pas importés.
                                    </div>
                                )}

                                {/* Toolbar */}
                                <div className="fp-res-toolbar">
                                    <div className="fp-search-wrap">
                                        <Search size={13} className="fp-search-icon"/>
                                        <input className="fp-search" placeholder="Matricule, nom, fichier…" value={query} onChange={e => setQuery(e.target.value)}/>
                                    </div>
                                    <div className="fp-filter-tabs">
                                        {[["all","Tous"],["ok","Valides"],["warning","Avert."],["error","Erreurs"]].map(([k,l]) => (
                                            <button key={k} className={`fp-filter-tab ${filter===k?"active":""}`} onClick={() => setFilter(k)}>{l}</button>
                                        ))}
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="fp-table-wrap">
                                    <table className="fp-table">
                                        <thead>
                                        <tr>
                                            <th>Statut</th>
                                            <th>Fichier</th>
                                            <th>Matricule</th>
                                            <th>Collaborateur</th>
                                            <th>Période</th>
                                            <th>Taille</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {filtered.map(r => (
                                            <tr key={r.id} className={`fp-row fp-row-${r.status}`}>
                                                <td><StatusIcon status={r.status}/></td>
                                                <td className="fp-td-name" title={r.name}>{r.name.length>30?r.name.slice(0,28)+"…":r.name}</td>
                                                <td><span className="fp-mono">{r.matricule}</span></td>
                                                <td className="fp-td-emp">{r.employe || <span className="fp-unknown">Inconnu</span>}</td>
                                                <td className="fp-muted">{r.periode}</td>
                                                <td className="fp-muted">{r.size}</td>
                                                <td>
                                                    <div className="fp-row-actions">
                                                        <button className="fp-icon-btn" title="Prévisualiser" onClick={() => setPreview(r)}><Eye size={13}/></button>
                                                        <button className="fp-icon-btn fp-icon-btn-del" title="Retirer" onClick={() => { removeFile(r.name); }}><Trash2 size={13}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Import button */}
                                <div className="fp-import-bar">
                                <span className="fp-import-info">
                                    {stats.ok + stats.warn} fiche{stats.ok+stats.warn>1?"s":""} seront importée{stats.ok+stats.warn>1?"s":""}
                                    {stats.errors > 0 && ` · ${stats.errors} ignorée${stats.errors>1?"s":""}`}
                                </span>
                                    <button
                                        className={`fp-btn fp-btn-primary fp-btn-import ${!canImport?"disabled":""}`}
                                        onClick={doImport}
                                        disabled={!canImport || importing}
                                    >
                                        {importing
                                            ? <><span className="fp-spin"/>&nbsp;Import en cours…</>
                                            : <><Upload size={14}/> Confirmer l'import</>
                                        }
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── Done screen ── */}
            {done && (
                <div className="fp-done">
                    <div className="fp-done-circle"><CheckCircle size={40}/></div>
                    <h2>Import terminé !</h2>
                    <p>{stats.ok + stats.warn} fiche{stats.ok+stats.warn>1?"s":""} importée{stats.ok+stats.warn>1?"s":""} avec succès.</p>
                    {stats.errors > 0 && <p className="fp-done-warn">{stats.errors} fiche{stats.errors>1?"s":""} ignorée{stats.errors>1?"s":""} (matricule inconnu).</p>}
                    <button className="fp-btn fp-btn-primary" style={{marginTop:20}} onClick={() => { setFiles([]); setResults([]); setStep(0); setDone(false); }}>
                        <RefreshCw size={13}/> Nouvel import
                    </button>
                </div>
            )}

            {/* ── Preview modal ── */}
            <Modal
                open={!!preview}
                title="Détail de la fiche"
                onClose={() => setPreview(null)}
                footer={<button className="fp-btn fp-btn-outline" onClick={() => setPreview(null)}>Fermer</button>}
            >
                {preview && (
                    <div className="fp-detail">
                        <div className="fp-detail-status">
                            <StatusIcon status={preview.status}/>
                            <StatusBadge status={preview.status}/>
                            <span className="fp-detail-msg">{preview.message}</span>
                        </div>
                        <div className="fp-detail-grid">
                            <div className="fp-detail-item"><span>Fichier</span><strong>{preview.name}</strong></div>
                            <div className="fp-detail-item"><span>Taille</span><strong>{preview.size}</strong></div>
                            <div className="fp-detail-item"><span>Matricule</span><strong className="fp-mono">{preview.matricule}</strong></div>
                            <div className="fp-detail-item"><span>Collaborateur</span><strong>{preview.employe || "—"}</strong></div>
                            <div className="fp-detail-item"><span>Période</span><strong>{preview.periode}</strong></div>
                        </div>
                        {preview.status === "error" && (
                            <div className="fp-alert fp-alert-danger" style={{marginTop:14}}>
                                <XCircle size={14}/> Ce fichier ne sera pas importé.
                            </div>
                        )}
                        {preview.status === "warning" && (
                            <div className="fp-alert fp-alert-warn" style={{marginTop:14}}>
                                <AlertTriangle size={14}/> Vérifiez avant de confirmer l'import.
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}